/* Reading product barcodes (EAN-13, EAN-8, UPC) from the camera or a photo.

   Two decoders:
     BarcodeDetector  built into Chrome on Android and some other platforms.
                      Fast and needs no code from us.
     ZXing            JavaScript decoder bundled into the app, used wherever
                      BarcodeDetector is missing: Windows Chrome, iPhone Safari,
                      Firefox.

   The camera only works on a secure origin: https, or localhost. */

import type { BrowserMultiFormatOneDReader, IScannerControls } from "@zxing/browser";
import type { ZXingModule } from "./zxing-entry";

/** Set at build time; versions the lazily loaded ZXing bundle's URL. */
declare const __BUILD_ID__: string;

/** The built-in detector. Not in TypeScript's DOM library yet. */
interface DetectedBarcode {
  rawValue: string;
}
interface NativeBarcodeDetector {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface NativeBarcodeDetectorClass {
  new (options: { formats: string[] }): NativeBarcodeDetector;
  getSupportedFormats(): Promise<string[]>;
}

/* Live scanning reads full-length codes only. A blurred or partly framed
   EAN-13 often decodes as a "valid" EAN-8 or UPC-E made of half its bars,
   which is where random-looking numbers come from. Indian packs use EAN-13
   (UPC-A is the same code with a leading 0). A still photo is one careful
   read, so it also accepts the short formats. */
type Mode = "live" | "photo";
const NATIVE_FORMATS: Record<Mode, string[]> = {
  live: ["ean_13", "upc_a"],
  photo: ["ean_13", "ean_8", "upc_a", "upc_e"],
};
const SCAN_INTERVAL_MS = 150;

/** Same number this many frames in a row before the camera accepts it. */
const AGREEING_READS = 3;

/** GTIN check digit (EAN-8, UPC-A, EAN-13): weights 3,1,3,1… from the right. */
export function validBarcode(code: string): boolean {
  if (!/^(\d{8}|\d{12}|\d{13})$/.test(code)) return false;
  const digits = [...code].map(Number);
  const check = digits.pop() ?? -1;
  const sum = digits.reverse().reduce((total, d, i) => total + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

/** Accepts a code only after it has been read identically several times
 *  running; a different code restarts the count. Frames with no read don't. */
function agreement(): (code: string) => boolean {
  let last = "";
  let count = 0;
  return (code) => {
    if (!validBarcode(code)) return false;
    count = code === last ? count + 1 : 1;
    last = code;
    return count >= AGREEING_READS;
  };
}

function nativeDetectorClass(): NativeBarcodeDetectorClass | null {
  const cls = (window as unknown as { BarcodeDetector?: NativeBarcodeDetectorClass }).BarcodeDetector;
  return cls ?? null;
}

/** Use the built-in detector only when it supports grocery barcodes. */
async function nativeDetector(mode: Mode): Promise<NativeBarcodeDetector | null> {
  const cls = nativeDetectorClass();
  if (!cls) return null;
  try {
    const supported = await cls.getSupportedFormats();
    const formats = NATIVE_FORMATS[mode].filter((f) => supported.includes(f));
    return formats.length ? new cls({ formats }) : null;
  } catch {
    return null;
  }
}

let zxingLoading: Promise<ZXingModule> | null = null;

/** Load dist/zxing.js on first use only. It is a separate ~480 KB bundle, so
 *  pages that never need it (Android, typed barcodes) never download it. */
function loadZXing(): Promise<ZXingModule> {
  const existing = (window as unknown as { CleanScoreZXing?: ZXingModule }).CleanScoreZXing;
  if (existing) return Promise.resolve(existing);
  zxingLoading ??= new Promise<ZXingModule>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "/zxing.js?v=" + __BUILD_ID__;
    script.onload = () => {
      const loaded = (window as unknown as { CleanScoreZXing?: ZXingModule }).CleanScoreZXing;
      if (loaded) resolve(loaded);
      else reject(new Error("The barcode decoder failed to load."));
    };
    script.onerror = () => {
      zxingLoading = null; // allow a retry, e.g. after reconnecting
      reject(new Error("Couldn't load the barcode decoder. Check your connection."));
    };
    document.head.appendChild(script);
  });
  return zxingLoading;
}

async function zxingReader(mode: Mode): Promise<BrowserMultiFormatOneDReader> {
  const { BarcodeFormat, BrowserMultiFormatOneDReader, DecodeHintType } = await loadZXing();
  const formats =
    mode === "live"
      ? [BarcodeFormat.EAN_13, BarcodeFormat.UPC_A]
      : [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E];
  const hints = new Map<number, unknown>([
    [DecodeHintType.POSSIBLE_FORMATS, formats],
    // Slower per frame, but finds bars that are small, tilted or slightly soft.
    [DecodeHintType.TRY_HARDER, true],
  ]);
  return new BrowserMultiFormatOneDReader(hints, { delayBetweenScanAttempts: 120 });
}

export function cameraAvailable(): boolean {
  return window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;
}

/** Explain a camera failure in words the user can act on. */
export function describeCameraError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError") return "Camera permission was refused. Allow it in the browser, or type the barcode.";
  if (name === "NotFoundError") return "No camera found. Type the barcode or choose a photo of it.";
  if (name === "NotReadableError") return "The camera is in use by another app.";
  if (!window.isSecureContext) return "The camera needs https. Type the barcode instead.";
  return "Couldn't start the camera. Type the barcode instead.";
}

/** A running camera scan. stop() releases the camera. */
export interface Scan {
  result: Promise<string>;
  /** Resolves true once the camera is running and has a torch to switch. */
  hasTorch: Promise<boolean>;
  setTorch(on: boolean): void;
  stop(): void;
}

/* Grocery barcodes have thin bars: the browser's default 640x480 stream is
   too coarse to resolve them, so ask for HD. */
const CAMERA: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
  audio: false,
};

/** Camera features Chrome exposes that TypeScript's DOM library lacks. */
interface ExtraCapabilities {
  focusMode?: string[];
  zoom?: { min: number; max: number };
  torch?: boolean;
}

/* Continuous autofocus, so a barcode held close comes sharp; and a little
   zoom, so the bars fill more pixels without the phone having to get closer
   than its lens can focus. Each is applied only where the camera offers it. */
async function tuneCamera(track: MediaStreamTrack): Promise<boolean> {
  const caps = (track.getCapabilities?.() ?? {}) as ExtraCapabilities;
  const advanced: Record<string, unknown>[] = [];
  if (caps.focusMode?.includes("continuous")) advanced.push({ focusMode: "continuous" });
  if (caps.zoom && caps.zoom.max >= 1.5) advanced.push({ zoom: Math.min(2, caps.zoom.max) });
  for (const constraint of advanced) {
    try {
      await track.applyConstraints({ advanced: [constraint] } as MediaTrackConstraints);
    } catch {
      /* not supported on this camera after all; carry on without it */
    }
  }
  return !!caps.torch;
}

/** Start the camera in `video` and resolve with the first barcode read. */
export function scanFromCamera(video: HTMLVideoElement): Scan {
  let stopped = false;
  let stream: MediaStream | null = null;
  let stopDecoder: () => void = () => {};
  let markTorch: (has: boolean) => void = () => {};
  const hasTorch = new Promise<boolean>((resolve) => (markTorch = resolve));

  const release = (): void => {
    stopDecoder();
    stream?.getTracks().forEach((track) => track.stop());
    stream = null;
  };

  const result = new Promise<string>((resolve, reject) => {
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia(CAMERA);
        if (stopped) return release();
        const track = stream.getVideoTracks()[0];
        markTorch(track ? await tuneCamera(track) : false);

        const agrees = agreement();
        const native = await nativeDetector("live");
        if (native) {
          video.srcObject = stream;
          await video.play();
          const tick = async (): Promise<void> => {
            if (stopped) return;
            try {
              const codes = await native.detect(video);
              const code = codes[0]?.rawValue ?? "";
              if (code && agrees(code)) {
                release();
                resolve(code);
                return;
              }
            } catch {
              /* frame not ready yet; try the next one */
            }
            window.setTimeout(() => void tick(), SCAN_INTERVAL_MS);
          };
          void tick();
          return;
        }

        // ZXing reads from the same tuned stream, so focus and zoom apply here too.
        const reader = await zxingReader("live");
        if (stopped) return release();
        const controls: IScannerControls = await reader.decodeFromStream(stream, video, (decoded, _error, ctl) => {
          const code = decoded?.getText() ?? "";
          if (code && !stopped && agrees(code)) {
            ctl.stop();
            release();
            resolve(code);
          }
        });
        stopDecoder = () => controls.stop();
        if (stopped) release();
      } catch (error) {
        markTorch(false);
        release();
        reject(error);
      }
    })();
  });

  return {
    result,
    hasTorch,
    setTorch(on: boolean) {
      const track = stream?.getVideoTracks()[0];
      void track?.applyConstraints({ advanced: [{ torch: on }] } as unknown as MediaTrackConstraints).catch(() => {});
    },
    stop() {
      stopped = true;
      release();
      video.srcObject = null;
    },
  };
}

/** Read a barcode from a photo, for laptops with no camera. */
export async function scanFromPhoto(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const native = await nativeDetector("photo");
    if (native) {
      try {
        const bitmap = await createImageBitmap(file);
        const codes = await native.detect(bitmap);
        bitmap.close();
        const code = codes.map((c) => c.rawValue).find(validBarcode);
        if (code) return code;
      } catch {
        /* fall back to ZXing below */
      }
    }
    const decoded = (await (await zxingReader("photo")).decodeFromImageUrl(url)).getText();
    if (!validBarcode(decoded)) throw new Error("misread");
    return decoded;
  } catch {
    throw new Error("No barcode found in that photo. Try a sharper, straight-on shot, or type the number.");
  } finally {
    URL.revokeObjectURL(url);
  }
}
